/**
 * What an aircraft is, from its heartbeat: the autopilot firmware and the
 * vehicle type.
 *
 * Two fields of the same HEARTBEAT: `autopilot` (MAV_AUTOPILOT) says whose
 * firmware, `type` (MAV_TYPE) says what kind of vehicle. ArduPilot ships a
 * separate firmware per kind, so the pill names that one; PX4 is one firmware
 * for everything. A boat runs ArduRover (FRAME_CLASS 2): the predecessor's
 * branch called it "ArduBoat", which does not exist, and an operator looking
 * for that firmware to flash would not find it.
 */

export type VehicleClass = 'copter' | 'plane' | 'rover' | 'boat' | 'sub' | 'unknown';

/** MAV_AUTOPILOT_ARDUPILOTMEGA and MAV_AUTOPILOT_PX4. */
export const AUTOPILOT_ARDUPILOT = 3;
export const AUTOPILOT_PX4 = 12;

const PLANE_TYPES = new Set([1, 16, 19, 20, 21, 22, 23, 24, 25]);
const COPTER_TYPES = new Set([2, 3, 4, 13, 14, 15, 29]);

export function vehicleClassFor(mavType: number | undefined): VehicleClass {
  if (mavType === undefined) return 'unknown';
  if (COPTER_TYPES.has(mavType)) return 'copter';
  if (PLANE_TYPES.has(mavType)) return 'plane';
  if (mavType === 10) return 'rover';
  if (mavType === 11) return 'boat';
  if (mavType === 12) return 'sub';
  return 'unknown';
}

/** Rovers and boats: no altitude worth showing, no takeoff, no numbered motors. */
export function isSurfaceVehicle(mavType: number | undefined): boolean {
  const kind = vehicleClassFor(mavType);
  return kind === 'rover' || kind === 'boat';
}

const CLASS_LABEL: Record<VehicleClass, string> = {
  copter: 'Copter',
  plane: 'Plane',
  rover: 'Rover',
  boat: 'Boat',
  sub: 'Sub',
  unknown: 'Unknown',
};

const ARDUPILOT_LABEL: Record<VehicleClass, string> = {
  copter: 'ArduCopter',
  plane: 'ArduPlane',
  rover: 'ArduRover',
  boat: 'ArduRover',
  sub: 'ArduSub',
  unknown: 'ArduPilot',
};

export interface VehicleKind {
  /** The pill text. */
  label: string;
  /** The longer form, for the tooltip. */
  title: string;
  vehicleClass: VehicleClass;
}

export function vehicleKind(
  mavType: number | undefined,
  autopilot: number | undefined,
): VehicleKind {
  const vehicleClass = vehicleClassFor(mavType);
  const known = vehicleClass !== 'unknown';
  const what = CLASS_LABEL[vehicleClass];

  if (autopilot === AUTOPILOT_PX4) {
    return { label: 'PX4', title: known ? `PX4 · ${what}` : 'PX4', vehicleClass };
  }
  if (autopilot === AUTOPILOT_ARDUPILOT) {
    return {
      label: ARDUPILOT_LABEL[vehicleClass],
      title: known ? `ArduPilot · ${what}` : 'ArduPilot, vehicle type not recognised',
      vehicleClass,
    };
  }
  if (known) {
    return { label: what, title: `${what}, firmware not reported`, vehicleClass };
  }
  return { label: 'MAV', title: 'Vehicle type not reported yet', vehicleClass };
}

/** Metres per second to knots: 1 kn is 1852 m per hour. */
export function knotsFromMs(ms: number): number {
  return (ms * 3600) / 1852;
}
